import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { DevicePermissionsProvider } from '@/lib/contexts/device-permissions-context';
import { useTheme } from '@/lib/contexts/theme-context';
import { useLocale } from '@/lib/contexts/locale-context';
import { Button } from '@/components/ui/button';
import { LocaleMenuItems } from '@/components/ui/app/locale-menu-items';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Layout pour les pages publiques (invitations, etc.)
 * Fournit le thème et les éléments communs sans la navigation authentifiée
 */
export default function PublicLayout() {
    const { theme } = useTheme();
    const { t } = useTranslation('public');
    const { locale, setLocale } = useLocale();

    return (
        <DevicePermissionsProvider>
            {/* body et #app ne défilent jamais (app.blade.php) : hauteur bornée et zone scrollable interne, comme AppLayout */}
            <div className="flex-1 flex flex-col min-h-0 bg-background">
                {/* Header minimal avec logo */}
                <header className="shrink-0 z-50 bg-card/80 backdrop-blur-sm border-b border-border">
                    <div className="max-w-4xl mx-auto px-4 py-3 relative flex items-center justify-center">
                        <a href={theme.links.website} target="_blank" rel="noopener noreferrer">
                            <img src={theme.logo} alt={theme.logoAlt} className="h-6 w-auto" />
                        </a>

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-2 top-1/2 -translate-y-1/2"
                                    aria-label={t('common:language.switch')}
                                >
                                    <Languages className="w-4 h-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuRadioGroup value={locale} onValueChange={setLocale}>
                                    <LocaleMenuItems />
                                </DropdownMenuRadioGroup>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </header>

                {/* Zone scrollable - prend tout l'espace entre le header et le footer */}
                <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                    <Outlet />
                </main>

                {/* Footer minimal */}
                <footer className="shrink-0 bg-card/80 backdrop-blur-sm border-t border-border">
                    <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-center gap-4">
                        <a
                            href={theme.links.legalNotice}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {t('legalNotice')}
                        </a>
                        <span className="text-border">|</span>
                        <a
                            href={theme.links.privacyPolicy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {t('privacyPolicy')}
                        </a>
                        <span className="text-border">|</span>
                        <span className="text-xs text-muted-foreground">
                            © {new Date().getFullYear()} {theme.name}
                        </span>
                    </div>
                </footer>
            </div>
        </DevicePermissionsProvider>
    );
}
